import React from 'react';
import { styled } from '@mui/material';
import { itemAttrs } from '../../../utils/itemAttrs';
import { useChartInteractionState } from '../../ChartInteractionProvider';
import { useChartAccessorsContext } from '../../ChartRoot';
import { highlighted } from '../Legend.styles';
import { useLegendInteractions } from '../useLegendInteractions';
import { TableLegendRowContextValue } from './TableLegend.types';
import { TableLegendRowContext } from './useTableLegendRow';

type TableLegendRowProps<D> = {
  datum: D;
  index: number;
  total: number;
} & React.ComponentProps<typeof StyledTableLegendLI>;

export const TableLegendRow = <D,>({ datum, index, total, children, ...props }: TableLegendRowProps<D>) => {
  const { getValue, getIdentifier, getColor, getSelected, labelFormatter } = useChartAccessorsContext();
  const { highlightedItem, isPinnable } = useChartInteractionState();
  const { onClick, onPointerEnter, onPointerLeave } = useLegendInteractions();
  const uniqueId = React.useId();

  const id = getIdentifier(datum);
  const value = getValue(datum);
  const color = getColor(datum);
  const label = labelFormatter?.(datum, { where: 'legend' }) ?? String(value);
  const isHighlighted = highlightedItem === id;
  const isSelected = getSelected ? getSelected(datum, index) : false;
  const ariaPrefix = `${uniqueId}${id}`;
  const ariaLabelId = `${ariaPrefix}_label`;
  const ariaValueId = `${ariaPrefix}_value`;
  const ariaPercentageId = `${ariaPrefix}_percent`;
  const ariaLabelledBy = [ariaLabelId, ariaValueId, ariaPercentageId].join(' ');

  const ctx = React.useMemo<TableLegendRowContextValue<D>>(
    () => ({ datum, index, id, value, total, color, label, highlighted: isHighlighted, selected: isSelected, ariaPrefix }),
    [datum, index, id, value, total, color, label, isHighlighted, isSelected, ariaPrefix]
  );

  return (
    <TableLegendRowContext value={ctx}>
      <StyledTableLegendLI
        {...itemAttrs({ id, index, highlighted: isHighlighted, selected: isSelected, interactive: isPinnable })}
        onPointerEnter={() => onPointerEnter(datum, id, index)}
        onPointerLeave={onPointerLeave}
        onClick={event => onClick(event, datum, id, index)}
        aria-labelledby={ariaLabelledBy}
        {...props}>
        {children}
      </StyledTableLegendLI>
    </TableLegendRowContext>
  );
};

const StyledTableLegendLI = styled('li')(() => ({
  gridColumn: '1 / -1',
  display: 'grid',
  gridTemplateColumns: 'subgrid',
  alignContent: 'center',
  padding: '5px 12px',
  alignItems: 'center',
  ...highlighted(),
}));
